/* eslint no-useless-escape: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from '../Icon';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';

import Checkmark from '../../svg/icons/checkmark.svg';
import CloseThick from '../../svg/icons/close-thick.svg';

import './PasswordCriteria.scss';

export const length = {
  translationKey: 'password_length',
  translationText: 'At least (8) characters',
  validation: (str) => str.length >= 8,
};
export const letter = {
  translationKey: 'password_letter',
  translationText: 'At least (1) letter',
  validation: (str) => /[A-Za-z]/.test(str),
};
export const number = {
  translationKey: 'password_number',
  translationText: 'At least (1) number',
  validation: (str) => /[0-9]/.test(str),
};
export const specialChar = {
  translationKey: 'password_special_char',
  translationText: 'At least (1) symbol (!?$^& etc.)',
  validation: (str) => /[!@#\$%\^\&*\)\(+=._-]/.test(str),
};

export const passwordCriteria = [
  length,
  number,
  letter,
  specialChar,
];

export const criteriaMet = (criteria, password) => criteria.reduce(
  (agg, val) => agg && val.validation(password),
  true
);

export default function PasswordCriteria({
  open,
  password,
  passwordConfirm,
  virgin,
}) {
  const allCriteriaMet = criteriaMet(passwordCriteria, password);
  const noMatch = allCriteriaMet && (passwordConfirm.length && (password !== passwordConfirm));
  const isPanelCollapsed = !allCriteriaMet || !passwordConfirm.length || password === passwordConfirm;
  const ariaLabelValid = getTranslation({
    translationKey: 'password_criteria_met',
    fallback: 'Met',
  });
  const ariaLabelInvalid = getTranslation({
    translationKey: 'password_criteria_not_met',
    fallback: 'Not Met',
  });
  const ariaLabelIncluded = getTranslation({
    translationKey: 'password_criteria_included',
    fallback: 'Included',
  });
  const ariaLabelNotIncluded = getTranslation({
    translationKey: 'password_criteria_not_included',
    fallback: 'Not Included',
  });

  function getLi(obj) {
    const valid = obj.validation(password);
    const ariaLabel = valid ? ariaLabelValid : ariaLabelInvalid;
    const ariaLabelForIncluded = valid ? ariaLabelIncluded : ariaLabelNotIncluded;

    return (
      <li key={obj.translationKey} className={cx({ PasswordCriteria__valid: valid })}>
        {(valid || virgin) && (
          <Icon
            component={Checkmark}
            name="check-thick"
            role="img"
            ariaLabel={ariaLabelForIncluded}
          />
        )}
        {!virgin && !valid && (
          <Icon
            component={CloseThick}
            name="close-thick"
            iconClass="invalid"
            ariaLabel={ariaLabel}
          />
        )}
        <TranslationProvider
          className={cx({
            PasswordCriteria__valid: valid,
            'PasswordCriteria__not-valid': password.length && !valid,
          })}
          translationKey={obj.translationKey}
        >
          {obj.translationText}
        </TranslationProvider>
      </li>
    );
  }

  return (
    <div className={cx(
      'PasswordCriteria',
      'CollapseablePanelContainer',
      {
        PasswordCriteria__closed: !open,
        PasswordCriteria__met: allCriteriaMet,
        PasswordCriteria__no_match: noMatch,
      }
    )}
    >
      {open && (
      <>
        <div
          className={cx(
            'CollapseablePanel',
            {
              CollapseablePanel__collapsed: !allCriteriaMet,
            }
          )}
          aria-hidden={!allCriteriaMet}
        >
          <div className="PasswordCriteria__success" role="alert" aria-live="assertive">
            <Icon component={Checkmark} name="check" />
            {
              allCriteriaMet
              && (
              <TranslationProvider translationKey="password_is_valid">
                Nice Password!
              </TranslationProvider>
              )
            }
          </div>
        </div>

        <div
          className={cx(
            'CollapseablePanel',
            {
              CollapseablePanel__collapsed: isPanelCollapsed,
            }
          )}
          aria-hidden={isPanelCollapsed}
        >
          <div className="PasswordCriteria__match">
            <TranslationProvider translationKey="passwords_must_match">
              Passwords Must Match
            </TranslationProvider>
          </div>
        </div>

        <div
          className={cx(
            'CollapseablePanel',
            {
              CollapseablePanel__collapsed: allCriteriaMet,
            }
          )}
          aria-hidden={isPanelCollapsed}
        >
          <TranslationProvider className="PasswordCriteria__prompt" translationKey="password_must">
            Passwords must contain:
          </TranslationProvider>
          <ul>
            {passwordCriteria.map(getLi)}
          </ul>
        </div>
      </>
      )}
    </div>
  );
}

PasswordCriteria.propTypes = {
  open: PropTypes.bool,
  password: PropTypes.string,
  passwordConfirm: PropTypes.string,
  virgin: PropTypes.bool,
};

PasswordCriteria.defaultProps = {
  open: true,
  password: '',
  passwordConfirm: '',
  virgin: true,
};
